/**
 * Home Page - Public Landing Page for lab.grras.tech
 * Hands-on Labs Platform for Linux, DevOps & Programming
 */

import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useCurrency } from '../hooks/useCurrency';
import AnnouncementBanner from '../components/AnnouncementBanner';
import { TerminalAnimation } from '../components/TerminalAnimation';
import { 
  Terminal, 
  BookOpen, 
  Award,
  ChevronRight,
  Play,
  Users,
  Zap,
  Shield,
  Clock,
  ArrowRight,
  Mail,
  Phone,
  Menu,
  X,
  MapPin,
  ExternalLink,
  Quote,
  ChevronDown,
  MessageCircle,
  CheckCircle,
  GraduationCap,
  Code2,
  FolderGit2,
  HelpCircle,
  Layers,
  Rocket
} from 'lucide-react';

// Logo URL
const GRRAS_LOGO = '/images/grras-logo.png';

// Tool/Lab logos
const LOGO_URLS = {
  REDHAT: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redhat/redhat-original.svg',
  LINUX: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg',
  DOCKER: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg',
  ANSIBLE: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ansible/ansible-original.svg',
  TERRAFORM: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg',
  GIT: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg',
  PYTHON: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg',
  C_LANG: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg',
  CPP: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg',
  MYSQL: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg',
  JAVA: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg',
  KUBERNETES: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg',
};

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isIndia } = useCurrency();
  
  // Format price based on location
  const formatPrice = (inr, usd) => isIndia ? `₹${inr}` : `$${usd}`;
  const [openFaq, setOpenFaq] = useState(null);

  // Animated Counter Hook
  const useCounter = (end, duration = 2000) => {
    const [count, setCount] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        },
        { threshold: 0.1 }
      );

      if (ref.current) {
        observer.observe(ref.current);
      }

      return () => observer.disconnect();
    }, [isVisible]);

    useEffect(() => {
      if (!isVisible) return;

      let startTime;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }, [isVisible, end, duration]);

    return { count, ref };
  };

  // Stats data
  const stats = [
    { label: 'Students Trained', value: 500, suffix: '+', icon: <GraduationCap className="w-6 h-6" /> },
    { label: 'Hands-on Labs', value: 244, suffix: '+', icon: <Terminal className="w-6 h-6" /> },
    { label: 'Years as Red Hat Partner', value: 18, suffix: '', icon: <Award className="w-6 h-6" /> },
    { label: 'Pass Rate', value: 95, suffix: '%', icon: <CheckCircle className="w-6 h-6" /> },
  ];

  // Testimonials from certified students
  const testimonials = [
    {
      name: 'Aaryan Saini',
      cert: 'RHCSA Certified',
      quote: 'Labs helped me a lot in exam preparation. Practicing on real VMs gave me confidence. Cleared in first attempt!',
      color: 'from-red-500 to-red-600',
      certImage: '/certificates/aaryan_rhcsa.png'
    },
    {
      name: 'Keerti Sharma',
      cert: 'RHCE Certified',
      quote: 'Ansible labs are very practical. I can directly apply what I learned here in my job. Best investment for career.',
      color: 'from-emerald-500 to-emerald-600',
      certImage: '/certificates/keerti_rhce.png'
    },
    {
      name: 'Sagar Choudhary',
      cert: 'Red Hat Containers Specialist',
      quote: 'DO188 labs helped me understand containers and Podman properly. Felt confident in the exam.',
      color: 'from-blue-500 to-cyan-500',
      certImage: '/certificates/sagar_containers.png'
    },
    {
      name: 'Ankit Choudhary',
      cert: 'OpenShift Administrator (EX280)',
      quote: 'Practicing in real environment is a game changer. The gap between theory and practice gets filled here.',
      color: 'from-purple-500 to-violet-600',
      certImage: '/certificates/ankit_openshift.png'
    },
  ];

  // FAQ data - English
  const faqs = [
    {
      question: 'How long can I access the VM?',
      answer: 'You get 24/7 full-time access. Practice anytime, anywhere - no time restrictions at all.'
    },
    {
      question: 'Do I get a fresh VM every session?',
      answer: 'Yes! Every session gives you a fresh VM. Feel free to experiment without worry - if something breaks, just start a new session!'
    },
    {
      question: 'When do I get access after payment?',
      answer: 'Instantly! Access is granted immediately after successful payment. No waiting period.'
    },
    {
      question: 'Is there a refund policy?',
      answer: 'Yes, if you are not satisfied, you can request a refund within 7 days. No questions asked.'
    },
  ];

  // Labs data - NO PRICES
  const linuxDevOpsLabs = [
    { 
      name: 'RH124', 
      title: 'Linux Fundamentals', 
      labs: 42, 
      free: 3,
      logo: LOGO_URLS.REDHAT,
      color: 'from-red-500 to-red-600'
    },
    { 
      name: 'RH134', 
      title: 'Linux Administration', 
      labs: 18,
      logo: LOGO_URLS.REDHAT,
      color: 'from-red-600 to-red-700'
    },
    { 
      name: 'SVRADM', 
      title: 'Server Administration', 
      labs: 15,
      logo: LOGO_URLS.REDHAT,
      color: 'from-red-700 to-red-800'
    },
    { 
      name: 'ANSIBLE', 
      title: 'Automation (RHCE)', 
      labs: 18,
      logo: LOGO_URLS.ANSIBLE,
      color: 'from-slate-600 to-slate-700'
    },
    { 
      name: 'DOCKER', 
      title: 'Containers & Docker', 
      labs: 18,
      logo: LOGO_URLS.DOCKER,
      color: 'from-blue-500 to-cyan-500'
    },
    { 
      name: 'TERRAFORM', 
      title: 'Infrastructure as Code', 
      labs: 18,
      logo: LOGO_URLS.TERRAFORM,
      color: 'from-purple-500 to-violet-600'
    },
    { 
      name: 'KUBERNETES', 
      title: 'Container Orchestration', 
      labs: 18,
      logo: LOGO_URLS.KUBERNETES,
      color: 'from-blue-400 to-indigo-600'
    },
    { 
      name: 'GIT', 
      title: 'Version Control', 
      labs: 18,
      logo: LOGO_URLS.GIT,
      color: 'from-orange-500 to-red-500'
    },
  ];

  // Programming Labs - NEW
  const programmingLabs = [
    { 
      name: 'Python', 
      title: 'Python Programming', 
      labs: 15,
      logo: LOGO_URLS.PYTHON,
      color: 'from-yellow-400 to-yellow-500',
      description: 'Variables, Loops, Functions, OOP, File I/O'
    },
    { 
      name: 'C', 
      title: 'C Programming', 
      labs: 15,
      logo: LOGO_URLS.C_LANG,
      color: 'from-blue-400 to-blue-600',
      description: 'Pointers, Arrays, Strings, Memory'
    },
    { 
      name: 'C++', 
      title: 'C++ Programming', 
      labs: 15,
      logo: LOGO_URLS.CPP,
      color: 'from-indigo-500 to-purple-600',
      description: 'OOP, STL, Inheritance, Polymorphism'
    },
    { 
      name: 'MySQL/MariaDB', 
      title: 'Database Administration', 
      labs: 15,
      logo: LOGO_URLS.MYSQL,
      color: 'from-orange-400 to-amber-600',
      description: 'CRUD, JOINs, Procedures, Backup, Security'
    },
    { 
      name: 'Core Java', 
      title: 'Zero to Hero', 
      labs: 15,
      logo: LOGO_URLS.JAVA,
      color: 'from-red-500 to-orange-500',
      description: 'OOP, Collections, Multithreading, JDBC'
    },
  ];

  // Features - NO Auto Verification
  const features = [
    {
      icon: <Terminal className="w-5 h-5" />,
      title: 'Real Virtual Machines',
      description: 'Practice on actual VMs, not containers. Real enterprise experience.'
    },
    {
      icon: <BookOpen className="w-5 h-5" />,
      title: '244+ Hands-on Labs',
      description: 'Linux, DevOps & Programming - structured labs for all levels.'
    },
    {
      icon: <Code2 className="w-5 h-5" />,
      title: 'Auto Code Verification',
      description: 'Programming labs with instant feedback. Write code, verify output.'
    },
    {
      icon: <Award className="w-5 h-5" />,
      title: 'Exam Practice',
      description: 'RHCSA exam-style questions with dual VM setup. Be exam-ready.'
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: 'Safe to Experiment',
      description: 'Break things, learn from mistakes. Fresh VM every session.'
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Announcement Banner - Sticky at top */}
      <div className="sticky top-0 z-[60]">
        <AnnouncementBanner />
      </div>
      
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Linux, DevOps, Programming & Database Labs | RHCSA, Python, C, MySQL Practice | Grras Labs</title>
        <meta name="description" content="Practice Linux, DevOps & Programming on real VMs. 244+ hands-on labs for RHCSA, RHCE, Ansible, Python, C, C++. Auto-verified code labs & exam practice. Start free!" />
        <meta name="keywords" content="RHCSA labs, RHCE practice, Python labs, C programming labs, C++ labs, Linux labs, DevOps labs, Ansible labs, coding practice, hands-on labs, VM labs, Red Hat certification, programming practice, Jaipur" />
        <meta property="og:title" content="Linux, DevOps & Programming Labs | Grras Labs" />
        <meta property="og:description" content="244+ hands-on labs for RHCSA, RHCE, Python, C, C++ & DevOps. Practice on real VMs with auto-verification. Start free!" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lab.grras.tech" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://lab.grras.tech" />
      </Helmet>

      {/* Navigation - Sticky below banner */}
      <nav className="sticky top-0 z-50 bg-slate-950/95 backdrop-blur-lg border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo */}
            <a href="/" className="flex items-center">
              <img src={GRRAS_LOGO} alt="Grras Labs" className="h-14 md:h-16" />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <a href="#labs" className="text-sm text-slate-300 hover:text-white transition-colors">Labs</a>
              <a href="#features" className="text-sm text-slate-300 hover:text-white transition-colors">Features</a>
              <a href="#exam" className="text-sm text-slate-300 hover:text-white transition-colors">Exam Practice</a>
              <a href="#programming" className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors font-medium">Programming</a>
              <a href="#projects" className="text-sm text-amber-400 hover:text-amber-300 transition-colors font-medium">Projects</a>
              <a href="#help" className="text-sm text-slate-300 hover:text-white transition-colors">Help Center</a>
              <a href="/login" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-300 hover:text-white transition-colors">Login</a>
              <a 
                href="/register" 
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-sm bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 rounded-lg font-semibold transition-all"
              >
                Start Free
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-slate-400 hover:text-white"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-800">
              <div className="flex flex-col gap-3">
                <a href="#labs" className="text-sm text-slate-300 hover:text-white py-2">Labs</a>
                <a href="#features" className="text-sm text-slate-300 hover:text-white py-2">Features</a>
                <a href="#exam" className="text-sm text-slate-300 hover:text-white py-2">Exam Practice</a>
                <a href="#programming" className="text-sm text-emerald-400 hover:text-emerald-300 py-2 font-medium">Programming</a>
                <a href="#projects" className="text-sm text-amber-400 hover:text-amber-300 py-2 font-medium">Projects</a>
                <a href="#help" className="text-sm text-slate-300 hover:text-white py-2">Help Center</a>
                <a href="/login" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-300 hover:text-white py-2">Login</a>
                <a 
                  href="/register" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2.5 text-sm bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg font-semibold text-center"
                >
                  Start Free
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 md:pt-24 pb-12 md:pb-16 px-4 sm:px-6 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/10 via-transparent to-transparent"></div>
        <div className="absolute top-10 left-1/4 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl"></div>
        <div className="absolute top-20 right-1/4 w-72 h-72 bg-red-500/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-6xl mx-auto relative">
          <div className="text-center max-w-3xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 border border-slate-700 rounded-full text-xs text-slate-300 mb-4">
              <Zap className="w-3 h-3 text-yellow-500" />
              <span>Real VMs • 244+ Labs • Code Verification</span>
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 leading-tight">
              <span className="text-white">Hands-on Labs for</span>
              <br />
              <span className="bg-gradient-to-r from-emerald-400 via-emerald-500 to-cyan-500 bg-clip-text text-transparent">
                Linux, DevOps & Programming
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-base sm:text-lg text-slate-400 mb-6 max-w-xl mx-auto">
              Practice RHCSA, RHCE, Python, C, C++ & DevOps tools on real VMs.
              <span className="text-emerald-400 font-medium"> Auto-verified code labs.</span>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
              <a 
                href="/register"
                target="_blank"
                rel="noopener noreferrer" 
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 rounded-lg font-semibold transition-all shadow-lg shadow-emerald-500/25 flex items-center justify-center gap-2"
              >
                <Play className="w-4 h-4" />
                Start Free Labs
              </a>
              <a 
                href="https://www.grras.tech" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-6 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
              >
                View Courses
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500">
              <div className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                <span>500+ Students</span>
              </div>
              <div className="flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" />
                <span>244+ Labs</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Code2 className="w-3.5 h-3.5" />
                <span>Auto Verified</span>
              </div>
            </div>
          </div>

          {/* Terminal Preview - Animated */}
          <TerminalAnimation />
        </div>
      </section>

      {/* RHCSA Exam Practice - HIGHLIGHTED */}
      <section id="exam" className="py-10 md:py-14 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-red-900/80 to-red-800/80 border-2 border-red-500/50 rounded-2xl p-6 md:p-8 relative overflow-hidden">
            {/* Highlight Badge */}
            <div className="absolute top-0 right-0 bg-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-bl-lg">
              POPULAR
            </div>
            
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
                <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center shrink-0">
                  <img src={LOGO_URLS.REDHAT} alt="Red Hat" className="w-10 h-10" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-1">RHCSA Exam Practice</h2>
                  <p className="text-slate-300 text-sm md:text-base">3 Complete Exam Sets • 45 Questions • Dual VM Setup</p>
                  <p className="text-slate-400 text-xs mt-1">Real exam environment with Student VM + Exam Server</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="text-center">
                  <span className="text-3xl md:text-4xl font-bold text-white">{formatPrice(499, 19)}</span>
                  <p className="text-xs text-slate-400">One-time payment</p>
                </div>
                <a 
                  href="/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-white text-red-600 hover:bg-slate-100 rounded-lg font-bold transition-colors"
                >
                  Start Exam Practice
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interview Prep Mock Tests - NEW SECTION */}
      <section className="py-10 md:py-14 px-4 sm:px-6 bg-slate-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-amber-900/60 to-orange-900/60 border-2 border-amber-500/40 rounded-2xl p-6 md:p-8 relative overflow-hidden">
            {/* NEW Badge */}
            <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
              NEW
            </div>
            
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shrink-0">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-1">Interview Prep Mock Tests</h2>
                  <p className="text-slate-300 text-sm md:text-base">30+ Tests • Linux, DevOps & Programming</p>
                  <p className="text-slate-400 text-xs mt-1">Crack your tech interviews with interview-style questions</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="text-center">
                  <div className="flex items-center gap-2">
                    <span className="text-3xl md:text-4xl font-bold text-white">{formatPrice(499, 19)}</span>
                  </div>
                  <p className="text-xs text-emerald-400">1 Free Test Included</p>
                </div>
                <a 
                  href="/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg font-bold transition-colors"
                >
                  Start Interview Prep
                </a>
              </div>
            </div>
            
            {/* Features */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white/10 rounded-lg px-3 py-2 text-center">
                <p className="text-xs text-amber-300 font-medium">Linux & DevOps</p>
              </div>
              <div className="bg-white/10 rounded-lg px-3 py-2 text-center">
                <p className="text-xs text-amber-300 font-medium">Python, C, C++</p>
              </div>
              <div className="bg-white/10 rounded-lg px-3 py-2 text-center">
                <p className="text-xs text-amber-300 font-medium">Instant Results</p>
              </div>
              <div className="bg-white/10 rounded-lg px-3 py-2 text-center">
                <p className="text-xs text-amber-300 font-medium">Unlimited Retakes</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Counter Section */}
      <section className="py-10 md:py-14 px-4 sm:px-6 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          {/* Red Hat Partner Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-red-900/50 to-red-800/50 border border-red-500/30 rounded-xl">
              <img src={LOGO_URLS.REDHAT} alt="Red Hat" className="w-8 h-8" />
              <div>
                <p className="text-sm font-semibold text-white">Red Hat Authorized Training Partner</p>
                <p className="text-xs text-red-300">Since 2007 • 18+ Years of Excellence</p>
              </div>
            </div>
          </div>

          {/* Animated Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat, index) => {
              const { count, ref } = useCounter(stat.value);
              return (
                <div 
                  key={index}
                  ref={ref}
                  className="text-center p-4 md:p-6 bg-slate-800/30 border border-slate-700/50 rounded-xl"
                >
                  <div className="w-10 h-10 mx-auto mb-3 bg-emerald-500/10 rounded-lg flex items-center justify-center text-emerald-400">
                    {stat.icon}
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-white mb-1">
                    {count}{stat.suffix}
                  </div>
                  <p className="text-xs md:text-sm text-slate-400">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Labs Section - NO PRICES */}
      <section id="labs" className="py-10 md:py-14 px-4 sm:px-6 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Hands-on Labs</h2>
            <p className="text-slate-400 text-sm md:text-base max-w-xl mx-auto">
              Practice on real VMs. Labs for Linux, DevOps & Programming.
            </p>
          </div>

          {/* Linux & DevOps Labs */}
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Terminal className="w-4 h-4" /> Linux & DevOps
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {linuxDevOpsLabs.map((lab, index) => (
                <div 
                  key={index}
                  className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden hover:border-slate-700 transition-all group"
                >
                  <div className={`p-3 bg-gradient-to-r ${lab.color}`}>
                    <div className="flex items-center justify-center">
                      <img src={lab.logo} alt={lab.name} className="w-6 h-6" />
                    </div>
                  </div>
                  <div className="p-3 text-center">
                    <h3 className="text-sm font-bold mb-0.5">{lab.name}</h3>
                    <p className="text-xs text-slate-400 mb-1">{lab.title}</p>
                    <span className="text-xs text-emerald-400">{lab.labs} Labs</span>
                    {lab.free > 0 && (
                      <span className="block text-xs text-yellow-400 mt-0.5">{lab.free} Free</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Programming Labs - NEW */}
          <div id="programming">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Code2 className="w-4 h-4" /> Programming
              <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-0.5 rounded-full font-medium">NEW</span>
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
              {programmingLabs.map((lab, index) => (
                <div 
                  key={index}
                  className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden hover:border-slate-700 transition-all group"
                >
                  <div className={`p-3 bg-gradient-to-r ${lab.color}`}>
                    <div className="flex items-center justify-center">
                      <img src={lab.logo} alt={lab.name} className="w-6 h-6" />
                    </div>
                  </div>
                  <div className="p-3 text-center">
                    <h3 className="text-sm font-bold mb-0.5">{lab.name}</h3>
                    <p className="text-xs text-slate-400 mb-1">{lab.title}</p>
                    <span className="text-xs text-emerald-400">{lab.labs} Labs</span>
                    <span className="block text-xs text-cyan-400 mt-0.5">Auto Verified</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - SMALLER */}
      <section id="features" className="py-10 md:py-14 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Why Practice Here?</h2>
            <p className="text-slate-400 text-sm">Real skills through hands-on practice</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-4 bg-slate-800/30 border border-slate-700/50 rounded-xl hover:border-emerald-500/30 transition-all"
              >
                <div className="w-9 h-9 bg-emerald-500/10 rounded-lg flex items-center justify-center text-emerald-400 mb-3">
                  {feature.icon}
                </div>
                <h3 className="text-sm font-semibold mb-1">{feature.title}</h3>
                <p className="text-xs text-slate-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-10 md:py-14 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Certified Students</h2>
            <p className="text-slate-400 text-sm">Real results from real practice</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {testimonials.map((t, index) => (
              <div 
                key={index}
                className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 relative"
              >
                <Quote className="w-6 h-6 text-slate-700 absolute top-3 right-3" />
                
                {/* Certificate Image */}
                <div className="mb-3 rounded-lg overflow-hidden border border-slate-700 bg-white">
                  <img 
                    src={t.certImage} 
                    alt={`${t.name} Certificate`}
                    className="w-full h-auto"
                  />
                </div>

                <p className="text-slate-300 text-sm mb-3 leading-relaxed">"{t.quote}"</p>
                <div>
                  <p className="font-semibold text-sm">{t.name}</p>
                  <p className="text-xs text-emerald-400">{t.cert}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real-World Projects Section */}
      <section id="projects" className="py-10 md:py-14 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-indigo-900/70 to-violet-900/70 border-2 border-indigo-500/40 rounded-2xl p-6 md:p-8 relative overflow-hidden">
            {/* Badge */}
            <div className="absolute top-0 right-0 bg-indigo-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
              NEW
            </div>
            
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-xl flex items-center justify-center shrink-0">
                  <FolderGit2 className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-1">Real-World Projects</h2>
                  <p className="text-slate-300 text-sm md:text-base">12+ Projects • Step-by-Step Guides • Build Your Portfolio</p>
                  <p className="text-slate-400 text-xs mt-1">Linux, DevOps, Cloud, Networking & Security — hands-on project experience</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="text-center">
                  <div className="flex items-baseline gap-1 justify-center">
                    <span className="text-3xl md:text-4xl font-bold text-white">{formatPrice(99, 2)}</span>
                    <span className="text-sm text-slate-400">/project</span>
                  </div>
                  <p className="text-xs text-indigo-300">One-time per project</p>
                </div>
                <a 
                  href="/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-white text-indigo-600 hover:bg-slate-100 rounded-lg font-bold transition-colors"
                >
                  Explore Projects
                </a>
              </div>
            </div>
            
            {/* Project Categories */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {[
                { name: 'Linux Admin', icon: <Terminal className="w-3.5 h-3.5" /> },
                { name: 'DevOps & CI/CD', icon: <Layers className="w-3.5 h-3.5" /> },
                { name: 'Cloud & AWS', icon: <Rocket className="w-3.5 h-3.5" /> },
                { name: 'Networking', icon: <Shield className="w-3.5 h-3.5" /> },
                { name: 'Security', icon: <CheckCircle className="w-3.5 h-3.5" /> },
              ].map((cat, i) => (
                <div key={i} className="bg-white/10 rounded-lg px-3 py-2 flex items-center justify-center gap-2">
                  <span className="text-indigo-300">{cat.icon}</span>
                  <p className="text-xs text-indigo-200 font-medium">{cat.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Help Center Section */}
      <section id="help" className="py-10 md:py-14 px-4 sm:px-6 bg-slate-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 md:p-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-center sm:text-left">
                <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center shrink-0">
                  <HelpCircle className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-lg md:text-xl font-bold mb-1">Need Help?</h2>
                  <p className="text-slate-400 text-sm">Support ticket system with expert assistance. Create a ticket and get help quickly.</p>
                </div>
              </div>
              <a 
                href="/register"
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 px-5 py-2.5 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 text-emerald-400 rounded-lg font-semibold text-sm transition-colors flex items-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                Visit Help Center
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10 md:py-14 px-4 sm:px-6 bg-slate-900/50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Frequently Asked Questions</h2>
            <p className="text-slate-400 text-sm">Got questions? We've got answers</p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-slate-800/30 border border-slate-700/50 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-800/50 transition-colors"
                >
                  <span className="font-medium text-sm">{faq.question}</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-4 text-sm text-slate-400">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 md:py-14 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to Practice?
          </h2>
          <p className="text-slate-400 text-sm md:text-base mb-6">
            Start with 3 free labs. No credit card required.
          </p>
          <a 
            href="/register"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 rounded-lg font-semibold transition-all shadow-lg shadow-emerald-500/25"
          >
            Create Free Account
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Footer - NO Courses section */}
      <footer className="py-8 px-4 sm:px-6 bg-slate-900 border-t border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Brand */}
            <div>
              <img src={GRRAS_LOGO} alt="Grras Labs" className="h-14 md:h-16 mb-3" />
              <p className="text-slate-400 text-sm mb-3">
                Hands-on labs for Linux, DevOps & Programming. Practice on real VMs with auto-verified code labs.
              </p>
              <a 
                href="https://www.grras.tech" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-emerald-400 text-sm hover:underline"
              >
                Visit grras.tech for courses
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-sm mb-3">Quick Links</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#labs" className="hover:text-white transition-colors">Linux & DevOps Labs</a></li>
                <li><a href="#programming" className="hover:text-white transition-colors">Programming Labs</a></li>
                <li><a href="#exam" className="hover:text-white transition-colors">Exam Practice</a></li>
                <li><a href="#projects" className="hover:text-white transition-colors">Real-World Projects</a></li>
                <li><a href="#help" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="/login" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Login</a></li>
                <li><a href="/register" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Sign Up</a></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold text-sm mb-3">Contact</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>
                  <a href="mailto:info@grras.tech" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Mail className="w-3.5 h-3.5" />
                    info@grras.tech
                  </a>
                </li>
                <li>
                  <a href="tel:+919001991227" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Phone className="w-3.5 h-3.5" />
                    +91-9001991227
                  </a>
                </li>
                <li className="flex items-start gap-2 pt-1">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>A-81, Singh Bhoomi, Khatipura Rd, behind Marudhar Hospital, Jaipur, Rajasthan 302012</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-800 text-center text-slate-500 text-xs">
            <p>© {new Date().getFullYear()} Grras Solutions. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
